import { useTranslation } from "react-i18next";

import { Dropdown } from "components";
import "components/DealersAndLocationsDropdown/DealersAndLocationsDropdown.scss";
import { useDealersAndLocationsDropdown } from "components/DealersAndLocationsDropdown/hooks";

import type { DropdownItemProps, DropdownProps } from "semantic-ui-react";

export type DealersLocationsDropdownValue = {
  dealerIds: number[];
  locationIds: number[];
};

const searchDealerLocations = (options: DropdownItemProps[], query: string) => {
  const search = query.toLowerCase();
  const filteredOptions: DropdownItemProps[] = [];
  let currentDealerHeader: DropdownItemProps | null = null;

  options.forEach(option => {
    const isDealerHeader = typeof option.value === "string" && option.value.startsWith("dealer-");
    const matchesSearch = typeof option.text === "string" && option.text.toLowerCase().includes(search);

    if (isDealerHeader) {
      currentDealerHeader = option;
      if (matchesSearch) {
        filteredOptions.push(option);
        currentDealerHeader = null;
      }
      return;
    }

    if (matchesSearch) {
      if (currentDealerHeader) {
        filteredOptions.push(currentDealerHeader);
        currentDealerHeader = null;
      }
      filteredOptions.push(option);
    }
  });

  return filteredOptions;
};

interface DealersAndLocationsDropdownProps extends DropdownProps {
  handleOnChange: ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => void;
  values: DealersLocationsDropdownValue;
  allowSelectAllDealers?: boolean;
  allowSelectDealers?: boolean;
  isAllDealersPreselected?: boolean;
}

export const DealersAndLocationsDropdown = ({
  handleOnChange,
  values,
  allowSelectAllDealers = true,
  allowSelectDealers = true,
  isAllDealersPreselected = false
}: DealersAndLocationsDropdownProps) => {
  const { t } = useTranslation();

  const { allDealersSelected, groupedLocationsOptions, handleOnSelectionChange } = useDealersAndLocationsDropdown({
    handleOnChange,
    allowSelectDealers,
    allowSelectAllDealers
  });

  const value =
    allDealersSelected || isAllDealersPreselected
      ? ["all"]
      : [...values.locationIds.map(l => "location-" + l), ...(allowSelectDealers ? values.dealerIds.map(d => "dealer-" + d) : [])];

  return (
    <Dropdown
      selectOnBlur={false}
      placeholder={allowSelectDealers ? t("v8_dealers_locations", "Dealers/Locations") : t("v8_locations", "Locations")}
      value={value}
      closeOnChange={false}
      options={groupedLocationsOptions}
      onChange={handleOnSelectionChange}
      fluid
      search={searchDealerLocations}
      multiple
      floating
      selection
      scrolling
      clearable
      renderLabel={(option: DropdownItemProps) => ({ content: ` ${option.labelText} `, icon: option.icon })}
    />
  );
};
